import classnames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { TabContent, TabPane, UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  ConfirmModal,
  LoadingMigrate,
  LoadingMigrateConfirm,
  LoadingResetStudy,
  ProgressLoading,
  CloseIcon as closeIcon,
  IcArtifactActive as icArtifactActive,
  IcArtifact as icArtifact,
} from '@/shared/ui';
import {
  ENV,
  ENV_ENUM,
  REPORT_TYPES,
  URL_CALLCENTER_PORTAL,
  IS_NOT_EDIT_REPORT_MESSAGE,
  TAB_KEY,
} from '@/shared/config';
import AiPDFReportTab from '@/widgets/pdf-report';
import BeatHR from '@/widgets/beats';
import HRSumary from '@/widgets/hr-summary';
import RhythmEvents from '@/widgets/ecg-events';
import StripsManagementV2 from '@/widgets/strips-management';

import { simulateKeyEvent } from '@/shared/lib';
import UndoState from './undoRedoListener';
import InitFromIndexDB from './hydrateReportState';
import StudyInfoDrawer from './studyInfoDrawer/studyInfoDrawer';
import PatientInfoDrawer from './patientInfoDrawer';
import FacilityInfoDrawer from './facilityInfoDrawer';

import CheckSavePrompt from './CheckSavePrompt';
import ReportHeaderWrapper from './ReportHeader/reportHeaderWrapper';
import ArtifactAreasTimeTracking from './artifactAreasTimeTracking/artifactAreasTimeTracking';
import RedoIcon from './assets/redo-icon.svg';
import UndoIcon from './assets/undo-icon.svg';
import DebugObserver from './debugObserver';
import PingAiApiSection from './pingAiApiSection/pingAiApiSection';
import StickerCommands from './sticker';
import TimeTrackingWithCondition from './timeTracking/timeTrackingWithCondition';

/*
  All hot keys:
  N,S,V,Q,Z,X,C,W,F,ESC,B,CTRL+A,CTRL+S,D,R,A,arrow up down left right,>
  N,S,V,Q: change beat type
  D: delete beat/event
  Z: previous page
  X: next page
  C: open change duration event
  W: open add new event
  F: open add bookmark
  R: mark/unmark event reviewed
  ESC: escape from mode
  B: toggle Add Remove Beat mode (ECGViewer)
  A: mark event as artifact
  CTRL+A: select all strip
  CTRL+S: Save data
  arrow up down left right: navigate through strip in page
  1,2,3,4,5,6: set bulk action beat
*/

import useAiReportPage from './useAiReportPage';

/**
 * Top-level page for an AI-processed cardiac study report, hosting the header toolbar, the study, patient and facility drawers, every confirmation modal and the beats, HR summary, rhythm events, strips and PDF report tabs. Takes all state from useAiReportPage, short-circuits to the reset-study, migration-confirm or migration-progress screens before the main layout, enables artifact-area time tracking only on staging or customer environments with an active profile, and renders each tab pane once websocket, beat and IndexedDB loading finish.
 *
 * @returns The rendered report page, or one of the reset, migration-confirmation and migration-progress screens.
 */
const AiReportPage = () => {
  const {
    studyFid,
    profileId,
    loading,
    isMacOS,
    state,
    setState,
    isNotEdit,
    isLoadIndexDB,
    isLoadBeatData,
    isWsConnected,
    isResetingStudy,
    isOpenWrongSomething,
    typeWrong,
    reportData,
    aiProcessing,
    ecgDataMap,
    isStartedAiProcessing,
    isOpenProcessingLimit,
    limitProcessingStudy,
    isShowTabHeader,
    isLoadedIndexDB,
    isUndoDisabled,
    isRedoDisabled,
    isDisabledBtn,
    migrateAiData,
    isGeneratingReport,
    studyId,
    facilityId,
    isAbortedStudy,
    timezoneOffset,
    beatsRef,
    ecgEventsRef,
    stripsRef,
    pdfReportRef,
    hrSummaryRef,
    onDiscardChangeSavedData,
    onKeepEditingWithSavedData,
    toggleDisplayStudyInfoDrawer,
    toggleDisplayPatientInfoDrawer,
    toggleDisplayFacilityInfoDrawer,
    onClickReload,
    onClickGoCallCenter,
    handleOnClickArtifactReport,
    navigateTab,
    onClickCancelNavigateTab,
    onClickNavigateWithoutSave,
    onClickConfirmNavigateTab,
    saveReportOnClick,
    onClickAcceptLimitProcessingStudy,
    checkDataChange,
    onClickConfirmMigrate403,
    handledDeleteUnusedDatabases,
    displayNoApiCallingModal,
    handleConfirmActivity,
    onClickConfirmNavigateV2Tab,
    limitUserProcessingContent,
    isOldVersion,
    shouldRenderPingAiApiSection,
  } = useAiReportPage();

  /**
   * Renders the toolbar control that marks or unmarks the current study report as an artifact report. Stays disabled for a holter profile already flagged as artifact or for a read-only report, opens the confirmation modal when marking and calls the artifact handler directly when unmarking, and picks the tooltip text from the holter-profile flag, the read-only state, or the report's current artifact flag.
   *
   * @returns The artifact icon button wrapped with its tooltip.
   */
  const renderMarkArtifact = () => (
    <div id="bt-artifact">
      <IconButton
        id="mark-report-as-artifact-btn"
        disabled={reportData?.holterProfile?.isArtifactReport || isNotEdit}
        iconComponent={
          <img
            src={
              reportData?.holterProfile?.isArtifactReport || reportData?.report?.isArtifactReport
                ? icArtifactActive
                : icArtifact
            }
            alt="Artifact icon"
          />
        }
        onClick={() => {
          if (!reportData?.report?.isArtifactReport) {
            setState({ isOpenConfirmArtifact: true });
          } else {
            handleOnClickArtifactReport(!reportData?.report?.isArtifactReport);
          }
        }}
      />
      <UncontrolledTooltip
        target="bt-artifact"
        className="custom-tooltip"
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        hideArrow
      >
        {(() => {
          if (reportData?.holterProfile?.isArtifactReport) {
            return 'Artifact report due to no beats and events detected';
          }
          if (isNotEdit) {
            return IS_NOT_EDIT_REPORT_MESSAGE;
          }
          return reportData?.report?.isArtifactReport
            ? 'Unmark report as artifact'
            : 'Mark report as artifact';
        })()}
      </UncontrolledTooltip>
    </div>
  );

  /**
   * Renders the undo control of the report header toolbar together with its keyboard-shortcut tooltip. Disables the button when the toolbar is globally disabled or the undo stack is empty, fires a simulated Ctrl+Z key event on click, and swaps the tooltip between the nothing-to-undo message and the macOS or Windows shortcut hint.
   *
   * @returns The undo icon button wrapped with its tooltip.
   */
  const renderUndoState = () => (
    <div id="bt-undo-btn">
      <IconButton
        id="undo-btn"
        className="more-icon me-3"
        isOutline
        disabled={isDisabledBtn ? true : isUndoDisabled}
        iconComponent={<img src={UndoIcon} alt="Undo icon" />}
        onClick={() => {
          simulateKeyEvent('Z', { ctrlKey: true });
        }}
      />
      <UncontrolledTooltip
        target="bt-undo-btn"
        className="custom-tooltip"
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        hideArrow
      >
        {isUndoDisabled ? 'There is nothing to undo' : isMacOS ? 'Undo  ⌘ Z' : 'Undo  Ctrl Z'}
      </UncontrolledTooltip>
    </div>
  );

  /**
   * Renders the redo control of the report header toolbar together with its keyboard-shortcut tooltip. Disables the button when the toolbar is globally disabled or the redo stack is empty, fires a simulated Ctrl+Y key event on click, and swaps the tooltip between the nothing-to-redo message and the macOS or Windows shortcut hint.
   *
   * @returns The redo icon button wrapped with its tooltip.
   */
  const renderRedoState = () => (
    <div id="bt-redo-btn">
      <IconButton
        id="redo-btn"
        className="more-icon me-3"
        isOutline
        disabled={isDisabledBtn ? true : isRedoDisabled}
        iconComponent={<img src={RedoIcon} alt="Redo icon" />}
        onClick={() => {
          simulateKeyEvent('Y', { ctrlKey: true });
        }}
      />
      <UncontrolledTooltip
        target="bt-redo-btn"
        className="custom-tooltip"
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        hideArrow
      >
        {isRedoDisabled ? 'There is nothing to redo' : isMacOS ? 'Redo  Shift ⌘ Z' : 'Redo  Ctrl Y'}
      </UncontrolledTooltip>
    </div>
  );

  if (isResetingStudy && !loading?.isLoadingInitial) {
    return <LoadingResetStudy onClick={onClickGoCallCenter} />;
  }

  // Display the migration confirmation screen when 403 occurs
  if (state.isShowConfirmMigrate403 && !migrateAiData?.isMigrating) {
    return <LoadingMigrateConfirm onConfirm={onClickConfirmMigrate403} />;
  }

  // Displays the running migration screen
  if (migrateAiData?.isMigrating) {
    return (
      <>
        <LoadingMigrate
          percentage={migrateAiData?.percentage || 0}
          estimateTime={migrateAiData?.estimateTime || 0}
          study={reportData?.study}
          migratingAt={reportData?.holterProfile?.migrateAiInfo?.migratingAt}
          // pre-existing gap: isUpdateMirage isn't part of the migrateAiData state shape.
          isUpdateMirage={(migrateAiData as any)?.isUpdateMirage}
        />
        {shouldRenderPingAiApiSection && (
          <PingAiApiSection profileId={profileId} activeTab={state.activeTab} isMigrating />
        )}
      </>
    );
  }

  // *: Do not track actime time in the following cases:
  // *: 1. Not in staging or customer environment
  // *: 2. There is no profileId
  // *: 3. AI processing is not enabled
  // *: 4. Report or findings generation is in progress
  const shouldTrackArtifactAreasTime =
    [ENV_ENUM.staging, ENV_ENUM.customer].includes(ENV) &&
    profileId &&
    aiProcessing &&
    !state.isExpiredToken;

  return (
    <>
      {shouldRenderPingAiApiSection && (
        <PingAiApiSection profileId={profileId} activeTab={state.activeTab} />
      )}
      <CheckSavePrompt isNotEdit={isNotEdit} condition={checkDataChange} />
      <StudyInfoDrawer
        visible={state.isOpenStudyInfoDrawer}
        title="Study Information"
        fetchStudyId={studyId}
        handleClickCloseButton={toggleDisplayStudyInfoDrawer}
      />
      <PatientInfoDrawer
        visible={state.isOpenPatientInfoDrawer}
        title="Patient Information"
        studyId={studyId}
        handleClickCloseButton={toggleDisplayPatientInfoDrawer}
      />
      <FacilityInfoDrawer
        visible={state.isOpenFacilityInfoDrawer}
        title="Facility Information"
        facilityId={facilityId}
        handleClickCloseButton={toggleDisplayFacilityInfoDrawer}
        isAbortedStudy={isAbortedStudy}
      />

      <ConfirmModal
        // isOpenProcessingLimitState (Recoil/index.ts) is declared atom<Boolean> (wrapper type) - pre-existing gap.
        isOpen={isOpenProcessingLimit as unknown as boolean}
        onClickRightButton={() => {
          document.location.href = URL_CALLCENTER_PORTAL;
        }}
        rightButtonName="Okay"
        title="Approaching processing limit"
        question={`The current limit for viewing and processing reports by AI has been exceeded.
          Please try again later.`}
        isRedBtn={false}
      />
      <ConfirmModal
        isOpen={state.isOpenWarningLimitStorage}
        onClickLeftButton={() => {
          handledDeleteUnusedDatabases();
          document.location.href = `${URL_CALLCENTER_PORTAL}studies/${studyId}`;
        }}
        onClickRightButton={onClickConfirmNavigateV2Tab}
        leftButtonName="Exit"
        rightButtonName="Save & exit"
        title="Out of storage space"
        question={`This PC is running out of space for processing. 
          If you would like to save changes (if any), press the “Save & Exit” button below.`}
      />
      <ConfirmModal
        isOpen={state.isOpenConfirmArtifact}
        onClickLeftButton={() => {
          setState({ isOpenConfirmArtifact: false });
        }}
        onClickRightButton={() => handleOnClickArtifactReport(true)}
        leftButtonName="No"
        rightButtonName="Yes"
        title="Mark report as artifact"
        question='Are you sure you want to mark this report as an "Artifact report"?'
      />
      <ConfirmModal
        isOpen={state.isOpenUseDbIndexModal}
        className="unsaved-change-modal --use-db-index"
        onClickLeftButton={onDiscardChangeSavedData}
        onClickRightButton={onKeepEditingWithSavedData}
        leftButtonName="Discard changes"
        rightButtonName="Keep editing"
        title="Unsaved changes"
        question="There are unsaved changes. If you would like to keep changes, press the “Keep editing” button below."
      />
      <ConfirmModal
        isOpen={limitProcessingStudy.isOpenModal}
        onClickRightButton={onClickAcceptLimitProcessingStudy}
        rightButtonName="Okay"
        title="Study processing limit reached"
        question={limitUserProcessingContent}
        isRedBtn={false}
        className="limit-study-per-user-modal"
        isFilledRightBtn={false}
      />
      <ConfirmModal
        isOpen={state.isOpenConfirmNavigateTab}
        className="unsaved-change-modal"
        onClickLeftButton={onClickNavigateWithoutSave}
        onClickRightButton={onClickConfirmNavigateTab}
        leftButtonName={state.isLoadingSaveAiReport ? null : 'Don’t Save'}
        rightButtonName="Save Changes"
        rightButtonWrapperId="unsaved-change-save-btn"
        rightButtonTooltip={isGeneratingReport ? 'Regenerating Report' : undefined}
        disabled={isGeneratingReport}
        isFilledRightBtn
        isBoldLeftBtn
        title={
          <div className="title-unsaved-change">
            <div>Unsaved changes</div>
            <IconButton
              iconComponent={<img src={closeIcon} alt="Close icon" />}
              onClick={onClickCancelNavigateTab}
            />
          </div>
        }
        question="You're about to leave this tab with unsaved changes. What would you like to do before proceeding?"
        isRedBtn={false}
        isSaving={state.isLoadingSaveAiReport}
      />
      <ConfirmModal
        isOpen={isOpenWrongSomething}
        onClickRightButton={() => (typeWrong === 1 ? onClickReload() : onClickGoCallCenter())}
        rightButtonName={typeWrong === 1 ? 'Reload page' : 'Okay'}
        title={typeWrong === 1 ? 'Sorry, something went wrong' : 'Oops!'}
        question={
          typeWrong === 1
            ? 'There’s an issue with the data. Please reload the page to continue.'
            : 'We’re unable to process the data right now. Please contact the support team for assistance.'
        }
        isRedBtn={false}
        className="wrong-something-per-user-modal"
        isFilledRightBtn={false}
      />
      <ConfirmModal
        isOpen={state.isOpenNoActivityModal}
        onClickRightButton={onClickReload}
        rightButtonName="Reload The Page"
        title="Notification"
        question="Since there has been no activity for the past 15 minutes, please reload the page to resume."
        isRedBtn={false}
      />
      <ConfirmModal
        className="no-api-calling-modal"
        isOpen={state.isOpenNoApiCallingModal}
        rightButtonName="Confirm"
        onClickRightButton={handleConfirmActivity}
        title="Notification"
        question="Since there has been no activity for the past 15 minutes, please confirm if you are still here."
        isRedBtn={false}
        isFilledRightBtn={false}
      />
      <ConfirmModal
        isOpen={state.isLoadedData && state.isOpenIndexDBError}
        onClickRightButton={onClickReload}
        rightButtonName="Reload page"
        title="Oops, something went wrong (#1)"
        question="Try reloading the page"
        isRedBtn={false}
        isFilledRightBtn={false}
        className="indexeddb-error-modal"
      />

      <div className="custom-page">
        <ReportHeaderWrapper
          isLoadedData={state.isLoadedData}
          title={
            reportData?.report?.type === REPORT_TYPES.INTERIM
              ? 'Interim report'
              : reportData?.report?.type === REPORT_TYPES.UNREVIEWED
                ? 'Instant report'
                : 'End of Use report'
          }
          isEndOfUseReport={_.includes(
            [REPORT_TYPES.AI_EOU, REPORT_TYPES.STUDY],
            reportData?.report?.type,
          )}
          conflictUsers={state.conflictUsers}
          toggleDisplayStudyInfoDrawer={toggleDisplayStudyInfoDrawer}
          toggleDisplayPatientInfoDrawer={toggleDisplayPatientInfoDrawer}
          toggleDisplayFacilityInfoDrawer={toggleDisplayFacilityInfoDrawer}
          markArtifactComponent={renderMarkArtifact()}
          isDisableSaveReport={isNotEdit || isAbortedStudy}
          onClickSaveReport={saveReportOnClick}
          isLoadingSaveAiReport={state.isLoadingSaveAiReport}
          isNotEdit={isNotEdit}
          profileId={profileId}
          studyId={studyId}
          isArtifactReport={reportData?.report?.isArtifactReport}
          activeTab={state.activeTab}
          navigateTab={navigateTab}
          isStartedAiProcessing={isStartedAiProcessing}
          isLoadIndexDB={isLoadedIndexDB}
          undoStateComponent={renderUndoState()}
          redoStateComponent={renderRedoState()}
          pendingProfiles={state.pendingProfiles}
          isGeneratingReport={isGeneratingReport}
        />
        {profileId && studyFid && aiProcessing && (
          <StickerCommands activeTab={state.activeTab} profileId={profileId} studyFid={studyFid} />
        )}
        {state.isLoadedData && !migrateAiData?.isMigrating && (
          <>
            {isLoadIndexDB && <InitFromIndexDB />}
            <TimeTrackingWithCondition
              activeTab={state.activeTab}
              profileId={profileId}
              studyFid={reportData.study?.friendlyId}
              reportType={reportData.report?.type}
              reportId={reportData.report?.reportId}
              reportData={reportData}
              displayNoApiCallingModal={displayNoApiCallingModal}
              aiProcessing={aiProcessing}
              isOpenNoApiCallingModal={state.isOpenNoApiCallingModal}
              isExpiredToken={state.isExpiredToken}
              isWsConnected={isWsConnected}
              isLoadBeatData={isLoadBeatData}
            />
            {shouldTrackArtifactAreasTime && (
              <ArtifactAreasTimeTracking
                activeTab={state.activeTab}
                profileId={profileId}
                studyFid={reportData.study?.friendlyId}
                reportType={reportData.report?.type}
                reportId={reportData.report?.reportId}
              />
            )}
            {/* component undo and redo */}
            <UndoState />
            <DebugObserver
              beatsRef={beatsRef}
              ecgEventsRef={ecgEventsRef}
              stripsRef={stripsRef}
              pdfReportRef={pdfReportRef}
              hrSummaryRef={hrSummaryRef}
            />
            <TabContent
              className={classnames(
                'study-report-top-tab-content',
                isShowTabHeader ? '' : '--hide-tab',
              )}
              activeTab={state.activeTab}
            >
              <TabPane tabId={TAB_KEY.BEATS}>
                {isWsConnected && !isLoadBeatData ? (
                  <BeatHR tabRef={beatsRef} activeTab={state.activeTab} />
                ) : (
                  <ProgressLoading />
                )}
              </TabPane>
              <TabPane tabId={TAB_KEY.HR_SUMMARY}>
                {isWsConnected && !isLoadBeatData && isLoadIndexDB ? (
                  <HRSumary tabRef={hrSummaryRef} activeTab={state.activeTab} />
                ) : (
                  <ProgressLoading />
                )}
              </TabPane>
              <TabPane tabId={TAB_KEY.ECG_EVENTS}>
                {isWsConnected && !isLoadBeatData ? (
                  <RhythmEvents tabRef={ecgEventsRef} activeTab={state.activeTab} />
                ) : (
                  <ProgressLoading />
                )}
              </TabPane>
              <TabPane tabId={TAB_KEY.STRIPS_MANAGEMENT}>
                {isLoadedIndexDB && !isLoadBeatData ? (
                  <StripsManagementV2
                    ref={stripsRef}
                    activeTab={state.activeTab}
                    isActive={state.activeTab === TAB_KEY.STRIPS_MANAGEMENT}
                    ecgDataMap={ecgDataMap}
                    reportData={reportData}
                    timezoneOffset={timezoneOffset}
                    isNotEdit={isNotEdit}
                    needStartedAI={isOldVersion}
                  />
                ) : (
                  <ProgressLoading />
                )}
              </TabPane>
              <TabPane tabId={TAB_KEY.PDF_REPORT}>
                <AiPDFReportTab
                  ref={pdfReportRef}
                  activeTab={state.activeTab}
                  isAbortedStudy={isAbortedStudy}
                  isNotEdit={isNotEdit}
                  isGeneratingNormalReportInCallCenter={state.isGeneratingNormalReportInCallCenter}
                />
              </TabPane>
            </TabContent>
          </>
        )}
      </div>
    </>
  );
};

export default AiReportPage;
